/**
 * Search terms that brought conversions (Jacob, 2026-09-25).
 *
 * The review proposes a CONVERTING search term only as an out-of-area term
 * (P0-4c, guard on), with the conversions in its reason; a converting
 * competitor term is held back since #3048. Such a row is shown for the
 * operator to decide on: it carries a "Converted N times" flag
 * (ConvertedBadge.tsx), it is never pre-selected, and no "Select all"
 * control picks it. The operator ticks it by hand if they still want it
 * excluded.
 *
 * Review of #3045 (findings B and F):
 * - "Converting" belongs to the SEARCH TERM, not the row. Rows are per
 *   campaign and ad group, and the review keeps the highest-cost one, so a
 *   term that converted in another campaign showed 0 on the row: unflagged,
 *   picked by Select all, Easy. The backend now sends the term's total
 *   (term_conversions / term_converting), and `withTermConversions` also
 *   folds every recommendation of the same term together, so an older
 *   payload is covered too.
 * - A fraction (0.33 from data-driven attribution) is a conversion: every
 *   check is `> 0`, never `>= 1`.
 */

interface WithConversions {
  search_term?: string;
  metrics: { conversions: number };
  term_conversions?: number | null;
  term_converting?: boolean | null;
}

function finiteOrZero(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/**
 * The backend's term key (converting_term_guard.normalize_term): lowercase,
 * apostrophes dropped, "&" read as "and", any other punctuation a space.
 * Google Ads ignores punctuation in a negative keyword, so "mr. rooter" and
 * "mr rooter" are one search term: if either converted, both are converting.
 */
export function normalizedSearchTerm(term: string | null | undefined): string {
  return (term ?? '')
    .toLowerCase()
    .replace(/['’‘`]/g, '')
    .replaceAll('&', ' and ')
    .match(/[\p{L}\p{N}]+/gu)
    ?.join(' ') ?? '';
}

/** The search term's conversions: the term total when known, else the row's. */
export function recConversions(rec: WithConversions): number {
  return Math.max(
    finiteOrZero(rec.metrics?.conversions),
    finiteOrZero(rec.term_conversions),
  );
}

/**
 * True when the search term converted in this window, in any campaign or ad
 * group. A fraction counts.
 */
export function isConvertingRec(rec: WithConversions): boolean {
  return rec.term_converting === true || recConversions(rec) > 0;
}

/**
 * Folds every recommendation of one search term together: if any of them
 * converted, all of them are converting, with the largest known total.
 * Returns the same array (and the same objects) when nothing changes, so it
 * is safe inside a memo.
 */
export function withTermConversions<T extends WithConversions>(recs: T[]): T[] {
  const byTerm = new Map<string, { sum: number; best: number; converting: boolean }>();
  recs.forEach((rec) => {
    const key = normalizedSearchTerm(rec.search_term);
    if (!key) return;
    const entry = byTerm.get(key) ?? { sum: 0, best: 0, converting: false };
    entry.sum += Math.max(0, finiteOrZero(rec.metrics?.conversions));
    entry.best = Math.max(entry.best, finiteOrZero(rec.term_conversions));
    entry.converting = entry.converting || isConvertingRec(rec);
    byTerm.set(key, entry);
  });
  let changed = false;
  const out = recs.map((rec) => {
    const entry = byTerm.get(normalizedSearchTerm(rec.search_term));
    if (!entry) return rec;
    const total = Math.max(entry.sum, entry.best);
    const converting = entry.converting || total > 0;
    if (
      (rec.term_converting === true) === converting
      && finiteOrZero(rec.term_conversions) === total
    ) {
      return rec;
    }
    changed = true;
    return { ...rec, term_conversions: total, term_converting: converting };
  });
  return changed ? out : recs;
}

/** Ids of the recommendations whose search term converted. */
export function convertingIds<T extends WithConversions & { id: string }>(
  recs: T[],
): Set<string> {
  return new Set(
    withTermConversions(recs)
      .filter((rec) => isConvertingRec(rec))
      .map((rec) => rec.id),
  );
}

/** "3", "2.5", "0.33", "0.04": a fraction never rounds down to "0.0". */
export function formatConversionCount(value: number): string {
  const n = finiteOrZero(value);
  if (Number.isInteger(n)) return String(n);
  if (Math.abs(n) >= 1) return String(Number(n.toFixed(1)));
  return String(Number(n.toPrecision(2)));
}

/** "Converted 3 times", "Converted 1 time", "Converted 0.33 times". */
export function convertedCopy(conversions: number): string {
  const n = finiteOrZero(conversions);
  if (n <= 0) return 'Converted in this date range';
  return `Converted ${formatConversionCount(n)} ${n === 1 ? 'time' : 'times'}`;
}

export const CONVERTED_TITLE =
  'This search term brought conversions in this date range, in this or ' +
  'another campaign. Core never picks it for you. Exclude it only if you ' +
  'are sure.';
